// src/widgets/core/WidgetContainer.tsx
import { Portal } from '@chakra-ui/react';
import { createRoot, Root } from 'react-dom/client';
import createCache, { EmotionCache } from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { WidgetConfig } from '../types/config';
import ConversationWidget from '../components/widget-conversation';
import FullPageConversation from '../components/widget-full-page-conversation';
import * as React from 'react';
import { Provider } from '@/components/ui/provider';

import { persistStorageService } from '@/features/auth/persist-storage';
import sentryInit from '@/lib/sentry';

export class WidgetContainer {
  private container: HTMLElement;
  private shadow: ShadowRoot;
  private config: WidgetConfig;
  private emotionCache: EmotionCache;
  private root: Root | null = null;
  private portalContainer: HTMLElement;
  private createdContainer = false;

  constructor(config: WidgetConfig) {
    sentryInit();
    // Validate required config values
    if (!config.apiHost) {
      console.error('WidgetContainer: apiHost is required');
      throw new Error('apiHost is required');
    }

    this.config = {
      ...config,
      isFullPage: config.isFullPage || false,
      hideHeader: config.hideHeader || false,
      hideConversationList: config.hideConversationList || false,
    };

    // Set up global environment variables for the widget context
    this.setupEnvironmentVariables();

    // Determine host container: embed into provided element or create one
    if (!this.config.isFullPage && this.config.elementId) {
      const host = document.getElementById(this.config.elementId);
      if (host) {
        this.container = host as HTMLElement;
        this.createdContainer = false;
      } else {
        // Fallback to creating our own when target not found
        this.container = document.createElement('div');
        this.createdContainer = true;
        this.container.id = 'conversations-widget-root';
      }
    } else {
      this.container = document.createElement('div');
      this.createdContainer = true;
      this.container.id = 'conversations-widget-root';
    }

    if (this.config.isFullPage) {
      // For full page mode, we still want shadow DOM isolation
      this.container.style.position = 'fixed';
      this.container.style.top = '0';
      this.container.style.left = '0';
      this.container.style.right = '0';
      this.container.style.width = '100vw';
      this.container.style.height = '100vh';
      this.container.style.zIndex = '9999';
      this.container.style.overflow = 'hidden';
      this.shadow = this.container.attachShadow({ mode: 'open' });
    } else {
      // In embedded mode, ensure host can contain the widget and that
      // our content can reliably size to the parent's box.
      // We don't assume the page author set any styles, so we provide
      // safe defaults that won't fight an explicit height/width.
      // Respect the host element sizing set by the integrator.
      // Only ensure a positioning context; do not override width/height/overflow.
      if (!this.container.style.position) {
        this.container.style.position = 'relative';
      }

      if (!this.container.shadowRoot) {
        this.shadow = this.container.attachShadow({ mode: 'open' });
      } else {
        this.shadow = this.container.shadowRoot as ShadowRoot;
      }
    }

    // Create portal container inside shadow DOM
    this.portalContainer = document.createElement('div');
    this.portalContainer.id = this.config.elementId
      ? this.config.elementId
      : 'chakra-portal-container';

    // Create emotion cache for shadow DOM with proper isolation
    this.emotionCache = createCache({
      key: 'conversations-widget',
      container: this.shadow as unknown as HTMLElement,
      stylisPlugins: [],
    });

    // Set organization and location data in localStorage
    this.setupOrganizationAndLocation();

    this.initialize();
  }

  private setupOrganizationAndLocation() {
    // Handle organization ID with proper structure
    if (this.config.organisation_id) {
      const selectedOrganisation = {
        id: this.config.organisation_id,
        name: 'Organization', // Default name as per requirement
      };
      persistStorageService.setPersistItem({
        name: 'selectedOrganisation',
        value: selectedOrganisation,
        stringify: true,
      });
      console.log(
        'Widget: Set selectedOrganisation in localStorage:',
        selectedOrganisation,
      );
    }

    // Handle location ID with proper structure
    if (this.config.location_id) {
      const selectedLocation = {
        id: this.config.location_id,
        name: 'Location', // Default name as per requirement
      };
      persistStorageService.setPersistItem({
        name: 'selectedLocation',
        value: selectedLocation,
        stringify: true,
      });
      console.log(
        'Widget: Set selectedLocation in localStorage:',
        selectedLocation,
      );
    }
  }

  private setupEnvironmentVariables() {
    // Set up widget-specific environment variables
    window.__WIDGET_ENV__ = {
      VITE_API_URL: this.config.apiHost || import.meta.env.VITE_API_URL || '',
      VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL || '',
      VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY || '',
      VITE_WHATSAPP_SERVER_URL: import.meta.env.VITE_WHATSAPP_SERVER_URL || '',
      VITE_WHATSAPP_SERVER_API_KEY:
        import.meta.env.VITE_WHATSAPP_SERVER_API_KEY || '',
      VITE_WHATSAPP_SERVER_WEBSOCKET_URL:
        import.meta.env.VITE_WHATSAPP_SERVER_WEBSOCKET_URL || '',
      VITE_SUPABASE_MESSAGE_ATTACHMENTS_BUCKET_PUBLIC:
        import.meta.env.VITE_SUPABASE_MESSAGE_ATTACHMENTS_BUCKET_PUBLIC || '',
      VITE_CHANNEL_WEBHOOK_URL: import.meta.env.VITE_CHANNEL_WEBHOOK_URL || '',
    };
  }

  private createWidgetWrapper() {
    // Chakra UI Portal expects a RefObject with a 'current' property
    const portalContainerRef = { current: this.portalContainer };
    const WidgetWrapper = ({ children }: { children: React.ReactNode }) => {
      return <Portal container={portalContainerRef}>{children}</Portal>;
    };
    return WidgetWrapper;
  }

  private initialize() {
    // If it's fullPage, prevent body scrolling
    if (this.config.isFullPage) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    }

    const root = document.createElement('div');
    root.className = 'conversations-widget-container';
    this.shadow.appendChild(root);

    // Add portal container to shadow DOM
    this.shadow.appendChild(this.portalContainer);

    // Create the widget wrapper component
    const WidgetWrapper = this.createWidgetWrapper();

    this.root = createRoot(root);
    this.root.render(
      <CacheProvider value={this.emotionCache}>
        <WidgetWrapper>
          <Provider>
            <div id="widget-portal-root">
              {this.config.isFullPage ? (
                <FullPageConversation config={this.config} />
              ) : (
                <ConversationWidget config={this.config} />
              )}
            </div>
          </Provider>
        </WidgetWrapper>
      </CacheProvider>,
    );

    if (this.config.isFullPage) {
      const viewport = document.querySelector('meta[name="viewport"]');
      if (!viewport) {
        const meta = document.createElement('meta');
        meta.name = 'viewport';
        meta.content =
          'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
        document.head.appendChild(meta);
      }
    }

    // Only append to body if we created the container ourselves
    if (this.createdContainer) {
      document.body.appendChild(this.container);
    }
  }

  public destroy() {
    if (this.root) {
      this.root.unmount();
    }

    // Clean up environment variables
    delete window.__WIDGET_ENV__;

    // Clean up localStorage entries set by the widget
    if (this.config.organisation_id) {
      persistStorageService.removePersistItem({
        name: 'selectedOrganisation',
      });
    }

    if (this.config.location_id) {
      persistStorageService.removePersistItem({
        name: 'selectedLocation',
      });
    }

    // Restore scrolling when widget is destroyed
    if (this.config.isFullPage) {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }

    // Also ensure mobile class is removed
    document.body.classList.remove('conversations-widget-mobile-open');

    this.container.remove();
  }

  public open() {
    // Dispatch custom event for the widget to listen to
    const event = new CustomEvent('widget:open');
    this.container.dispatchEvent(event);
  }

  public close() {
    if (this.createdContainer) {
      this.container.style.display = 'none';
    }
    // Dispatch custom event for the widget to listen to
    const event = new CustomEvent('widget:close');
    this.container.dispatchEvent(event);
  }

  public getConfig(): WidgetConfig {
    return this.config;
  }

  // Method to update organization and location dynamically
  public updateOrganizationAndLocation(
    organisation_id?: string,
    location_id?: string,
  ) {
    if (organisation_id) {
      this.config.organisation_id = organisation_id;
      const selectedOrganisation = {
        id: organisation_id,
        name: 'Organization',
      };
      persistStorageService.setPersistItem({
        name: 'selectedOrganisation',
        value: selectedOrganisation,
        stringify: true,
      });
    }

    if (location_id) {
      this.config.location_id = location_id;
      const selectedLocation = {
        id: location_id,
        name: 'Location',
      };
      persistStorageService.setPersistItem({
        name: 'selectedLocation',
        value: selectedLocation,
        stringify: true,
      });
    }
  }

  public setAuthToken(accessToken: string, refreshToken: string) {
    persistStorageService.setPersistItem({
      name: 'token',
      value: accessToken,
    });
    persistStorageService.setPersistItem({
      name: 'refresh_token',
      value: refreshToken,
    });
  }

  public setApiKey(apiKey: string) {
    persistStorageService.setPersistItem({
      name: 'apiKey',
      value: apiKey,
    });
  }
}

// Update global interface
declare global {
  interface Window {
    __WIDGET_ENV__?: {
      VITE_API_URL: string;
      VITE_CHANNEL_WEBHOOK_URL: string;
      VITE_SUPABASE_URL: string;
      VITE_SUPABASE_ANON_KEY: string;
      VITE_WHATSAPP_SERVER_URL: string;
      VITE_WHATSAPP_SERVER_API_KEY: string;
      VITE_WHATSAPP_SERVER_WEBSOCKET_URL: string;
      VITE_SUPABASE_MESSAGE_ATTACHMENTS_BUCKET_PUBLIC: string;
    };
  }
}
